import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DESTINATIONS,
  TEST_MANAGEMENT_TESTS,
} from '@elementary/common-ui/constants/queryKeys';
import useAlertDestinations from 'shared/hooks/useAlertDestinations';
import useInvalidateAlertsRelatedData from 'shared/hooks/useInvalidateAlertsRelatedData';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/api';
import {
  ConfigurationType,
  IntegrationType,
  TestManagementGqlTestsResponse,
} from 'shared/types';
import { AlertIntegrationType } from '@elementary/common-ui/services/gql/types';

type CreateEditAlertOverrideFormProps = {
  ruleId?: string;
  alertOverride: {
    channelName: string;
    destination: string;
  };
};

type Props = {
  onDeleteSuccess?: () => void;
};

const useCustomAlertOverride = ({ onDeleteSuccess }: Props = {}) => {
  const { openSnackbar } = useSnackbar();
  const envId = useSelectedEnv();
  const { selectedId } = useSidePanelParams();
  const queryClient = useQueryClient();

  const {
    alertDestinationOptions,
    alertProviders,
    getDestinationItem,
    alertDestinationsError,
    alertDestinationsLoading,
  } = useAlertDestinations();

  const { invalidateAlertsDataQueries } = useInvalidateAlertsRelatedData();

  const {
    isLoading: isCreateCustomAlertOverrideLoading,
    mutateAsync: createCustomAlertOverrideMutation,
    error: createCustomAlertOverrideError,
  } = useMutation(
    (data: {
      alertOverride: CreateEditAlertOverrideFormProps['alertOverride'][];
      existingOverrides?: CreateEditAlertOverrideFormProps['alertOverride'][];
      entityIds: string[];
    }) => {
      const payload = [
        ...(data?.alertOverride ? data.alertOverride : []),
        ...(data?.existingOverrides || []),
      ].map((override) => {
        const { channelName, destination } = override || {};
        const integrationId =
          alertDestinationOptions?.[destination as IntegrationType]?.id || '';
        return {
          channel_name: channelName,
          alert_integration_id: integrationId,
        };
      });

      return api.createCustomAlertOverride(envId!, payload, data.entityIds);
    },
    {
      onSuccess: (_, payload) => {
        if (selectedId) {
          invalidateAlertsDataQueries(payload.entityIds);
        } else {
          // Update the tests list with alert destinations changed so table data
          // will be updated, because invalidation takes too long.
          queryClient.setQueryData(
            [TEST_MANAGEMENT_TESTS, envId, DESTINATIONS],
            (old?: TestManagementGqlTestsResponse) => ({
              tests:
                old?.tests.map((test) => {
                  if (payload.entityIds.includes(test.id)) {
                    return {
                      ...test,
                      alertDestinations: payload.alertOverride.map(
                        (override) => ({
                          id: '', // This is a temp ID, the data invalidation will update it
                          ruleId: '', // same
                          channel: override.channelName,
                          type: override.destination as AlertIntegrationType,
                          configurationType: 'CUSTOM' as ConfigurationType,
                        }),
                      ),
                    };
                  }
                  return test;
                }) || [],
            }),
          );

          invalidateAlertsDataQueries(payload.entityIds);
        }

        if (payload.alertOverride.length === 1) {
          const destinationItem = getDestinationItem(
            payload.alertOverride[0].destination as IntegrationType,
            payload.alertOverride[0].channelName,
          );

          const destinationName = Array.isArray(destinationItem)
            ? destinationItem.map((item) => item.label).join(', ')
            : destinationItem?.label;

          openSnackbar({
            message: `Custom alert override set to #${destinationName}`,
            variant: 'success',
          });
        } else {
          openSnackbar({
            message: `Custom alert overrides set for ${payload.entityIds.length} tests`,
            variant: 'success',
          });
        }
      },
    },
  );

  const {
    isLoading: isDeleteCustomAlertOverrideLoading,
    mutate: deleteCustomAlertOverrideMutation,
  } = useMutation(
    ({
      destinationId,
      entityIds,
    }: {
      destinationId: string;
      entityIds: string[];
    }) => api.deleteAlertDestinations(envId!, [destinationId], entityIds),
    {
      onSuccess: (_, payload) => {
        if (selectedId) invalidateAlertsDataQueries(payload.entityIds);
        else invalidateAlertsDataQueries(payload.entityIds);
        onDeleteSuccess?.();
      },
    },
  );

  return {
    alertDestinationOptions,
    alertProviders,
    alertDestinationsError,
    alertDestinationsLoading,
    getDestinationItem,
    customAlertOverride: {
      isCreateCustomAlertOverrideLoading,
      createCustomAlertOverrideMutation,
      createCustomAlertOverrideError,
      isDeleteCustomAlertOverrideLoading,
      deleteCustomAlertOverrideMutation,
    },
  };
};

export default useCustomAlertOverride;
